<template>
    <AppLayout>
        <div class="container p-4 mx-auto md:px-1 md:py-4">
            <!-- breadcrumb -->
            <Breadcrumb
                :title="$tc('LBL_CREDIT')"
                :items="[{ label: $tc('LBL_CREDIT_REQUEST', 2) }]"
                :home-label="$t('LBL_HOME')"
                :home-url="route('landing')"
            />
            <!-- breadcrumb end -->

            <div
                class="w-full bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-overflow"
            >
                <!-- Search & Action Bar -->
                <div class="flex flex-wrap items-center justify-between gap-4 p-4 sm:flex-nowrap">
                    <!-- Search Box -->
                    <div class="relative w-full sm:w-auto">
                        <label
                            for="table-search"
                            class="sr-only"
                            >Search</label
                        >
                        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg
                                class="w-5 h-5 text-gray-500 dark:text-gray-400"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                        </div>
                        <t-input
                            id="user-search-box"
                            v-model="search"
                            type="text"
                            variant="success"
                            class="w-full sm:w-80 pl-10 pr-4 py-2.5 text-sm rounded-lg border border-gray-300 focus:ring-1 focus:ring-green-500 focus:outline-none dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                            placeholder="Search for user"
                            @keypress.enter="fetchPageData()"
                        />
                    </div>
                </div>

                <div
                    class="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-none shadow-md"
                >
                    <div class="overflow-x-auto">
                        <table
                            class="min-w-[700px] w-full table-auto text-sm sm:text-base text-left text-gray-700 dark:text-gray-300"
                        >
                            <thead class="bg-gray-100 dark:bg-gray-700 text-sm font-semibold">
                                <tr>
                                    <!-- <th class="px-4 py-3">#</th> -->
                                    <th class="px-4 py-3">RETAILER</th>
                                    <th class="px-4 py-3">AMOUNT</th>
                                    <th class="px-4 py-3">CURRENT BALANCE</th>
                                    <th class="px-4 py-3">REQUESTED AT</th>
                                    <th class="px-4 py-3">MESSAGE</th>
                                    <th class="px-4 py-3">ACTIONS</th>
                                </tr>
                            </thead>
                            <tbody>
                                <template v-for="(row, rowIndex) in creditRequests">
                                    <tr
                                        :class="[
                                            'border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition duration-200',
                                            row.credit ? 'bg-green-200' : '',
                                        ]"
                                    >
                                        <!-- <td class="px-4 py-3">{{ rowIndex + 1 + (currentPage - 1) * perPage }}</td> -->
                                        <td
                                            v-if="!isRetailer"
                                            class="px-4 py-3"
                                        >
                                            {{ row.user.name }}
                                        </td>
                                        <td
                                            v-if="!isWholesaler"
                                            class="px-4 py-3"
                                        >
                                            {{ row.wholesaler.name }}
                                        </td>
                                        <td class="px-4 py-3">
                                            {{ config(common.currency) }} {{ parseFloat(row.amount).toFixed(2) }}
                                        </td>
                                        <td class="px-4 py-3">
                                            <div v-if="row.credit">
                                                {{ config(common.currency) }} {{ row.credit.credit_balance }}
                                            </div>
                                            <div v-else>--</div>
                                        </td>
                                        <td class="px-4 py-3">
                                            {{ row.created_at | fromNow }}
                                        </td>

                                        <td class="px-4 py-3">
                                            {{ row.message }}
                                        </td>
                                        <td
                                            v-if="!isAdmin"
                                            class="px-4 py-3 flex items-center"
                                        >
                                            <div
                                                v-if="!row.accepted_at && !row.deleted_at && isWholesaler"
                                                class="px-4 py-3 flex gap-2"
                                            >
                                                <t-button
                                                    variant="success"
                                                    @click="viewRequest(row)"
                                                >
                                                    View
                                                </t-button>
                                            </div>

                                            <div v-else>
                                                <div
                                                    v-if="row.accepted_at && !row.deleted_at"
                                                    class="inline-block px-3 py-2 text-md text-white bg-green-500 rounded"
                                                >
                                                    Accepted
                                                </div>
                                                <div
                                                    v-if="!row.accepted_at && row.deleted_at"
                                                    class="inline-block px-3 py-2 text-md text-white bg-red-500 rounded"
                                                >
                                                    Rejected
                                                </div>
                                                <div
                                                    v-if="!row.accepted_at && !row.deleted_at"
                                                    class="inline-block px-3 py-2 text-md text-white bg-yellow-500 rounded"
                                                >
                                                    Pending
                                                </div>
                                            </div>

                                            <div
                                                v-if="!isAdmin"
                                                class="ml-6 cursor-pointer"
                                                @click.stop="
                                                    goto('chat.show', {
                                                        recipientId: isWholesaler ? row.user_id : row.wholesaler_id,
                                                    })
                                                "
                                            >
                                                <ChatIcon class="w-5 duration-200 transform hover:scale-125" />
                                            </div>
                                        </td>
                                    </tr>
                                </template>
                                <tr
                                    v-if="!creditRequests.length"
                                    class="text-center"
                                >
                                    <td
                                        colspan="6"
                                        class="px-4 py-4 text-gray-500 dark:text-gray-400"
                                    >
                                        {{ $t('LBL_DATA_FOUND') }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </AppLayout>
</template>

<script>
    import _ from 'lodash';
    import AppLayout from '../../Layouts/AppLayout.vue';
    import ChatIcon from '../../Components/Icons/ChatIcon.vue';
    import common from '../../common';
    import Breadcrumb from '@/Components/Breadcrumb.vue';

    export default {
        name: 'CreditRequestIndex',

        components: {
            AppLayout,
            ChatIcon,
            Breadcrumb,
        },

        props: {
            creditRequests: {
                type: Array,
                required: true,
            },
            oldSearch: {
                type: String,
                default: null,
            },
        },

        data() {
            return {
                common,
                tableHeaders: [
                    'Requested By',
                    'Requested To',
                    'Amount',
                    'Message',
                    'Current Balance',
                    'Submitted At',
                    'Action',
                ],
                search: this.oldSearch,
            };
        },

        computed: {
            currentTableHeaders() {
                if (this.isRetailer) {
                    return this.tableHeaders.filter((each) => each !== 'Requested By');
                }
                if (this.isWholesaler) {
                    return this.tableHeaders.filter((each) => each !== 'Requested To');
                }
                return this.tableHeaders.filter((each) => each !== 'Action');
            },
        },

        watch: {
            search: {
                handler() {
                    this.debounceFetchSearchResults();
                },
            },
        },

        mounted() {
            this.debounceFetchSearchResults = _.debounce(this.fetchSearchResults, 1000);
        },

        methods: {
            // acceptRequest(request) {
            //     console.log(request)
            //     this.$inertia.get(route('creditRequest.approveRequest', request.id));
            // },

            // rejectRequest(request) {
            //     console.log(request)
            //     this.$inertia.delete(route('creditRequest.destroy', request.id));
            // },

            viewRequest(request) {
                this.$inertia.get(route('creditRequest.create'), request);
            },

            fetchSearchResults(page) {
                this.$inertia.get(
                    route('creditRequest.index', {
                        page,
                        search: this.search,
                    }),
                );
            },
        },
    };
</script>
